/**
 * 文件说明：统一处理列表 slot 引用按钮的点击/拖拽交互。
 *
 * 单格按钮和批量工具栏按钮都属于“从列表节点的若干 slot 发起引用连接”：
 * 点击时打开创建菜单，拖拽超过阈值后才启动 React Flow 原生连线流程。
 */
import { useCallback, useEffect, useRef, type DragEvent as ReactDragEvent, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react'
import {
  beginSlotReferenceConnectIntent,
  clearSlotReferenceConnectIntent,
  requestOpenSlotReferenceGuide,
  type SlotReferenceConnectIntent,
} from './slot-reference-connect-intent'

const SOURCE_LOGICAL_HANDLE_ID = 'canvas-source-logical'
const CLICK_DRAG_THRESHOLD_PX = 4

type PointerLike = {
  clientX: number
  clientY: number
}

type SlotReferenceSourceKind = SlotReferenceConnectIntent['sourceKind']

type PointerState = {
  dragStarted: boolean
  intent: SlotReferenceConnectIntent
  pointer: PointerLike
  pointerId: number
}

export interface UseSlotReferenceLinkInteractionOptions {
  sourceNodeId: string
  sourceKind: SlotReferenceSourceKind
  getSlotIndexes: () => readonly number[]
}

/**
 * 计算按钮在浏览器视口内的中心点。
 *
 * @param button 当前触发引用连接的按钮
 * @returns 按钮中心 client 坐标
 */
function resolveButtonCenter(button: HTMLButtonElement) {
  const rect = button.getBoundingClientRect()
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  }
}

function getMovedDistance(start: PointerLike, end: PointerLike) {
  return Math.hypot(end.clientX - start.clientX, end.clientY - start.clientY)
}

function normalizeSlotIndexes(slotIndexes: readonly number[]) {
  return Array.from(new Set(
    slotIndexes.filter((slotIndex) => Number.isInteger(slotIndex) && slotIndex >= 0),
  ))
}

/**
 * 把按钮拖拽转发给 React Flow 的节点级 source handle。
 *
 * @param sourceNodeId 发起引用的列表节点 ID
 * @param pointer 当前 pointer 位置
 */
function forwardPointerToSourceHandle(sourceNodeId: string, pointer: PointerLike) {
  const handleElement = document.querySelector<HTMLElement>(
    `.react-flow__handle[data-nodeid="${sourceNodeId}"][data-handleid="${SOURCE_LOGICAL_HANDLE_ID}"]`,
  )

  if (!handleElement) {
    return false
  }

  handleElement.dispatchEvent(new MouseEvent('mousedown', {
    bubbles: true,
    button: 0,
    buttons: 1,
    cancelable: true,
    clientX: pointer.clientX,
    clientY: pointer.clientY,
    composed: true,
    view: window,
  }))
  return true
}

/**
 * 构造 slot 引用连接意图。
 *
 * @param button 当前按钮，用于确定菜单和预览线起点
 * @param options 引用连接来源配置
 * @returns 有可用 slot 时返回 intent，否则返回 null
 */
function buildSlotReferenceIntent(
  button: HTMLButtonElement,
  options: UseSlotReferenceLinkInteractionOptions,
): SlotReferenceConnectIntent | null {
  const slotIndexes = normalizeSlotIndexes(options.getSlotIndexes())
  if (slotIndexes.length === 0) {
    return null
  }

  return {
    previewOrigin: resolveButtonCenter(button),
    sourceKind: options.sourceKind,
    sourceNodeId: options.sourceNodeId,
    slotIndexes,
  }
}

/**
 * 复用 slot 引用点击/拖拽交互。
 *
 * @param options source 节点、资源类型和当前要引用的 slot 下标集合
 * @returns 可直接绑定到 button 的事件与拖拽属性
 */
export function useSlotReferenceLinkInteraction(options: UseSlotReferenceLinkInteractionOptions) {
  const pointerStateRef = useRef<PointerState | null>(null)
  const suppressNextClickRef = useRef(false)
  const cleanupPointerListenersRef = useRef<() => void>(() => {})

  const cleanupPointerListeners = useCallback(() => {
    cleanupPointerListenersRef.current()
    cleanupPointerListenersRef.current = () => {}
  }, [])

  useEffect(() => cleanupPointerListeners, [cleanupPointerListeners])

  const openGuideFromIntent = useCallback((intent: SlotReferenceConnectIntent) => {
    requestOpenSlotReferenceGuide(intent)
  }, [])

  const openGuideFromButton = useCallback((button: HTMLButtonElement) => {
    const intent = buildSlotReferenceIntent(button, options)
    if (!intent) {
      return
    }
    openGuideFromIntent(intent)
  }, [openGuideFromIntent, options])

  const startDragConnect = useCallback((pointer: PointerLike) => {
    const state = pointerStateRef.current
    if (!state || state.dragStarted) {
      return
    }

    beginSlotReferenceConnectIntent(state.intent)
    const forwarded = forwardPointerToSourceHandle(options.sourceNodeId, pointer)
    if (!forwarded) {
      clearSlotReferenceConnectIntent()
      return
    }
    state.dragStarted = true
  }, [options.sourceNodeId])

  const finishPointerInteraction = useCallback((pointerId: number, shouldOpenGuide: boolean) => {
    const state = pointerStateRef.current
    if (!state || state.pointerId !== pointerId) {
      return
    }

    pointerStateRef.current = null
    cleanupPointerListeners()
    suppressNextClickRef.current = true

    if (shouldOpenGuide && !state.dragStarted) {
      openGuideFromIntent(state.intent)
    }
  }, [cleanupPointerListeners, openGuideFromIntent])

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!event.isPrimary || event.button !== 0) {
      return
    }

    const intent = buildSlotReferenceIntent(event.currentTarget, options)
    if (!intent) {
      return
    }

    event.stopPropagation()
    cleanupPointerListeners()
    pointerStateRef.current = {
      dragStarted: false,
      intent,
      pointer: event.nativeEvent,
      pointerId: event.pointerId,
    }

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const state = pointerStateRef.current
      if (!state || state.pointerId !== moveEvent.pointerId) {
        return
      }

      if (getMovedDistance(state.pointer, moveEvent) > CLICK_DRAG_THRESHOLD_PX) {
        // 超过阈值才启动 React Flow 连线，短点击只打开创建菜单。
        startDragConnect(moveEvent)
      }
    }

    const handlePointerUp = (upEvent: PointerEvent) => {
      finishPointerInteraction(upEvent.pointerId, true)
    }

    const handlePointerCancel = (cancelEvent: PointerEvent) => {
      finishPointerInteraction(cancelEvent.pointerId, false)
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp, { once: true })
    window.addEventListener('pointercancel', handlePointerCancel, { once: true })
    cleanupPointerListenersRef.current = () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerCancel)
    }
  }, [cleanupPointerListeners, finishPointerInteraction, options, startDragConnect])

  const onClick = useCallback((event: ReactMouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false
      return
    }

    // 支持键盘触发或无 pointer 事件的点击场景。
    openGuideFromButton(event.currentTarget)
  }, [openGuideFromButton])

  const onDragStart = useCallback((event: ReactDragEvent<HTMLElement>) => {
    event.preventDefault()
  }, [])

  return {
    draggable: false,
    onClick,
    onDragStart,
    onPointerDown,
  }
}
